"use client";

import { yesBtnCls } from "@/components/platform/style";
import { cn } from "@/lib/utils";
import { useEventConnect } from "@/store/eventConnect";
import { isGuessExpired } from "@/utils/expiration";
import { getGuessDisplayInfo } from "@/utils/guess";
import { formatCentPriceAsChancePercent, resolveUnitPriceNumber } from "@/utils/price";
import { formatUnitPrice, get } from "@/utils/utils";
import { BigMath, moveDrawGuessToCenter } from "@yn/utils";
import type { MouseEvent } from "react";

export type FootballMainGuessOptionsDisplayMode = "progress" | "aggregation";
export type FootballMainAggregationOptionSize = "compact" | "regular";

export interface FootballMainGuessOptionItem {
    guessCategoryId?: number | string | null;
    guessGroupNo?: string | null;
    guessNo?: string | null;
    groupTitle?: string | null;
    id?: string | number;
    status?: number | string | null;
    title?: string | null;
    yesRealtimePrice?: number | string | null;
    [key: string]: unknown;
}

export interface FootballMainGuessOptionsProps<TGuess extends FootballMainGuessOptionItem> {
    aggregationOptionSize?: FootballMainAggregationOptionSize;
    className?: string;
    disabled?: boolean;
    displayMode: FootballMainGuessOptionsDisplayMode;
    eventEnded?: boolean;
    guesses: TGuess[];
    onTradeClick: (
        event: MouseEvent<HTMLButtonElement>,
        guess: TGuess,
        selectedOption: "yes" | "no",
        originalIndex: number,
    ) => void;
    registerButton?: ( id: string, element: HTMLButtonElement | null ) => void;
    selectedIndex?: number;
}

const DEFAULT_UNIT_PRICE = 50;
const AGGREGATION_OPTION_HEIGHT_CLASS: Record<FootballMainAggregationOptionSize, string> = {
    compact: "h-10",
    regular: "h-12",
};

function getGuessTitle( guess: FootballMainGuessOptionItem ) {
    return String(
        get( guess, "title", "" ) || get( guess, "guessTitle", "" ) || get( guess, "groupTitle", "" ) || "",
    );
}

function getGuessLabel( guess: FootballMainGuessOptionItem ) {
    const guessTitle = getGuessTitle( guess ) || "-";
    const { title, subTitle } = getGuessDisplayInfo( {
        guessCategoryId: Number( get( guess, "guessCategoryId", 0 ) ),
        guessGroupNo: String( get( guess, "guessGroupNo", "" ) || "" ),
        groupTitle: String( get( guess, "groupTitle", "" ) || "" ),
        guessTitle,
    } );

    return { subTitle, title: title || guessTitle };
}

function getPriceValue( guess: FootballMainGuessOptionItem, key: "yesRealtimePrice" | "noRealtimePrice" ) {
    return resolveUnitPriceNumber( get( guess, key, DEFAULT_UNIT_PRICE ) );
}

function getBarWidth( guess: FootballMainGuessOptionItem ) {
    const price = getPriceValue( guess, "yesRealtimePrice" );
    if ( BigMath.lt( price, 0 ) ) return "0%";
    if ( BigMath.gt( price, 100 ) ) return "100%";
    return `${BigMath.toFixed( price, 2 )}%`;
}

function getButtonId( visualIndex: number ) {
    return visualIndex === 0 ? "yes-button" : `yes-button-${visualIndex}`;
}

function isDisabledGuess( guess: FootballMainGuessOptionItem, disabled?: boolean, eventEnded?: boolean ) {
    const status = Number( get( guess, "status", 0 ) );
    return !!disabled || !!eventEnded || status === 2 || status === 3 || isGuessExpired( guess );
}

function getOrderedGuesses<TGuess extends FootballMainGuessOptionItem>( guesses: TGuess[] ) {
    const indexedGuesses = guesses.map( ( guess, originalIndex ) => ( { guess, originalIndex } ) );
    return moveDrawGuessToCenter( indexedGuesses, ( item ) => getGuessTitle( item.guess ) );
}

function ProgressGuessOption<TGuess extends FootballMainGuessOptionItem>(
    { disabled, eventEnded, guess, onTradeClick, originalIndex, registerButton, visualIndex }: {
        disabled?: boolean;
        eventEnded?: boolean;
        guess: TGuess;
        onTradeClick: FootballMainGuessOptionsProps<TGuess>["onTradeClick"];
        originalIndex: number;
        registerButton?: FootballMainGuessOptionsProps<TGuess>["registerButton"];
        visualIndex: number;
    },
) {
    const guessEnded = isDisabledGuess( guess, disabled, eventEnded );
    const { title } = getGuessLabel( guess );
    const yesButtonId = getButtonId( visualIndex );

    return (
        <div className="flex min-h-9 w-full items-center gap-4">
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
                <div className="truncate text-sm font-medium capitalize text-t1">{title}</div>
                <div className="relative h-1 w-full overflow-hidden rounded-full bg-surface-subtle">
                    <div
                        className="absolute left-0 top-0 h-full rounded-full bg-yes"
                        style={{ width: getBarWidth( guess ) }}
                    />
                </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
                <div className="w-10 text-right text-base font-semibold text-t1">
                    {formatCentPriceAsChancePercent( getPriceValue( guess, "yesRealtimePrice" ) )}
                </div>
                {!guessEnded
                    ? (
                        <button
                            type="button"
                            ref={( element ) => registerButton?.( yesButtonId, element )}
                            disabled={guessEnded}
                            onClick={( e ) => onTradeClick( e, guess, "yes", originalIndex )}
                            className={cn(
                                "flex h-8 w-20 items-center justify-center gap-1 rounded px-2 text-center disabled:opacity-40",
                                yesBtnCls,
                            )}
                        >
                            <span className="text-xs font-medium leading-none">Yes</span>
                            <span className="text-xs font-semibold leading-none">
                                {formatUnitPrice( getPriceValue( guess, "yesRealtimePrice" ), { precision: 0 } )}
                            </span>
                        </button>
                    )
                    : null}
            </div>
        </div>
    );
}

function AggregationGuessOption<TGuess extends FootballMainGuessOptionItem>(
    { disabled, eventEnded, guess, isSelected, onTradeClick, originalIndex, registerButton, size, visualIndex }: {
        disabled?: boolean;
        eventEnded?: boolean;
        guess: TGuess;
        isSelected: boolean;
        onTradeClick: FootballMainGuessOptionsProps<TGuess>["onTradeClick"];
        originalIndex: number;
        registerButton?: FootballMainGuessOptionsProps<TGuess>["registerButton"];
        size: FootballMainAggregationOptionSize;
        visualIndex: number;
    },
) {
    const marketPrice = useEventConnect( ( state ) => state.guesses[String( get( guess, "guessNo", "" ) )] );
    const yesRealtimePrice = marketPrice?.yesPrice ?? get( guess, "yesRealtimePrice", DEFAULT_UNIT_PRICE );
    const { subTitle, title } = getGuessLabel( guess );
    const buttonId = getButtonId( visualIndex );
    const guessEnded = isDisabledGuess( guess, disabled, eventEnded );

    return (
        <button
            type="button"
            ref={( element ) => registerButton?.( buttonId, element )}
            disabled={guessEnded}
            aria-pressed={isSelected}
            onClick={( e ) => {
                e.stopPropagation();
                onTradeClick( e, guess, "yes", originalIndex );
            }}
            className={cn(
                "flex min-w-0 flex-1 items-center justify-center rounded-lg px-2 transition-transform active:scale-95 disabled:cursor-not-allowed disabled:opacity-40",
                AGGREGATION_OPTION_HEIGHT_CLASS[size],
                visualIndex === 0 && "bg-yes-subtle text-yes",
                visualIndex === 1 && "bg-surface-muted text-t2",
                visualIndex === 2 && "bg-no-subtle text-no",
                visualIndex > 2 && "bg-surface-subtle text-t2",
            )}
        >
            <span className="flex min-w-0 items-baseline gap-1 whitespace-nowrap text-sm font-medium leading-normal">
                <span className="truncate">{title}</span>
                {subTitle ? <span className="shrink-0">{subTitle}</span> : null}
                <span className="shrink-0 font-semibold">
                    {formatUnitPrice( resolveUnitPriceNumber( yesRealtimePrice ), { precision: 0 } )}
                </span>
            </span>
        </button>
    );
}

export function FootballMainGuessOptions<TGuess extends FootballMainGuessOptionItem>(
    {
        aggregationOptionSize = "compact",
        className,
        disabled,
        displayMode,
        eventEnded,
        guesses,
        onTradeClick,
        registerButton,
        selectedIndex,
    }: FootballMainGuessOptionsProps<TGuess>,
) {
    if ( !guesses || guesses.length === 0 ) return null;

    const orderedGuesses = getOrderedGuesses( guesses );

    if ( displayMode === "progress" ) {
        return (
            <div className={cn( "flex w-full flex-col gap-2", className )}>
                {orderedGuesses.map( ( item, visualIndex ) => (
                    <ProgressGuessOption
                        key={get( item.guess, "guessNo", "" ) || get( item.guess, "id", item.originalIndex )}
                        disabled={disabled}
                        eventEnded={eventEnded}
                        guess={item.guess}
                        originalIndex={item.originalIndex}
                        registerButton={registerButton}
                        visualIndex={visualIndex}
                        onTradeClick={onTradeClick}
                    />
                ) )}
            </div>
        );
    }

    return (
        <div className={cn( "flex w-full gap-2", className )}>
            {orderedGuesses.map( ( item, visualIndex ) => (
                <AggregationGuessOption
                    key={get( item.guess, "guessNo", "" ) || get( item.guess, "id", item.originalIndex )}
                    disabled={disabled}
                    eventEnded={eventEnded}
                    guess={item.guess}
                    isSelected={item.originalIndex === selectedIndex}
                    originalIndex={item.originalIndex}
                    registerButton={registerButton}
                    size={aggregationOptionSize}
                    visualIndex={visualIndex}
                    onTradeClick={onTradeClick}
                />
            ) )}
        </div>
    );
}
